import { AlloggiamentoFiltroDto, NodoDto } from "@/api";
import Box from "@/elements/Box";
import Typography from "@/elements/Typography";
import Card from "@mui/material/Card";
import SettingsIcon from "@mui/icons-material/Settings";
import IconButton from "@mui/material/IconButton";
import Table from "@/elements/Table";
import AlloggiamentiFiltroDialog from "./AlloggiamentiFiltroDialog";
import Button from "@/elements/Button";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import { ReactNode, useMemo, useState } from "react";
import StoricoAlloggiamentoFiltroDialog from "@/components/CentraleDetailsPage/StoricoAlloggiamentoFiltroDialog";
import { useAuth } from "@/context/useAuth";
import { Icon, Tooltip } from "@mui/material";
import { ScadenzaFiltroWarningDays } from "@/utils/scadenzeUtils";
import WarningIcon from "@/assets/warning.png";
import ErrorIcon from "@/assets/error.png";
import { getFiltroTypeHelper } from "@/utils/filtroTypeUtils";
import { useTranslation } from "react-i18next";

const columnHelper = createColumnHelper<AlloggiamentoFiltroDto>();

export default function AlloggiamentiFiltroCard({
  alloggiamentiFiltro,
  nodo,
}: {
  alloggiamentiFiltro: AlloggiamentoFiltroDto[];
  nodo: NodoDto;
}) {
  const { hasPermission } = useAuth();
  const { t, i18n } = useTranslation();

  const [alloggiamentiFiltroDialogOpen, setAlloggiamentiFiltroDialogOpen] =
    useState(false);
  const [storicoFiltriDialogOptions, setStoricoFiltriDialogOptions] = useState<{
    open: boolean;
    alloggiamento?: AlloggiamentoFiltroDto;
  }>({ open: false });

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor("nome", {
          header: t("alloggiamentiFiltroCard.colonne.alloggiamento"),
          size: 100,
          cell: ({
            row: {
              original: { filtroType, nome },
            },
          }) => {
            const typeHelper = getFiltroTypeHelper(filtroType, i18n.language);
            return (
              <Typography sx={{ display: "flex", alignItems: "center" }}>
                <Icon sx={{ mr: 1 }}>{typeHelper?.icon}</Icon> {nome}
              </Typography>
            );
          },
        }),
        columnHelper.accessor(
          (af) =>
            af.filtroAttuale
              ? `${af.filtroAttuale.modelloFiltro?.marca} - ${af.filtroAttuale.modelloFiltro?.nome}`
              : null,
          {
            header: t("alloggiamentiFiltroCard.colonne.filtro"),
            size: 100,
          }
        ),
        columnHelper.accessor(
          (af) =>
            af.filtroDaSostituireDopoIl
              ? moment(af.filtroDaSostituireDopoIl).format("L")
              : null,
          {
            header: t("alloggiamentiFiltroCard.colonne.scadenza"),
            size: 100,
            cell: ({
              row: {
                original: { filtroDaSostituireDopoIl },
              },
              getValue,
            }) => {
              let icon: ReactNode | null = null;
              if (filtroDaSostituireDopoIl) {
                const days = moment(filtroDaSostituireDopoIl).diff(
                  moment(),
                  "days",
                  true
                );
                icon =
                  days <= 0 ? (
                    <img
                      src={ErrorIcon}
                      width={20}
                      alt={t("alloggiamentiFiltroCard.alt.daSostituire")}
                    />
                  ) : days <= ScadenzaFiltroWarningDays ? (
                    <img
                      src={WarningIcon}
                      width={20}
                      alt={t("alloggiamentiFiltroCard.alt.inScadenza")}
                    />
                  ) : null;
              }

              return (
                <Box display="flex" gap={1} alignItems="center">
                  {getValue()}
                  {icon}
                </Box>
              );
            },
          }
        ),

        columnHelper.display({
          id: "buttons",
          header: "",
          maxSize: 100,
          cell: ({ row }) => {
            return (
              <Button
                variant="outlined"
                size="small"
                color="primary"
                onClick={() => {
                  setStoricoFiltriDialogOptions({
                    open: true,
                    alloggiamento: row.original,
                  });
                }}
              >
                {t("alloggiamentiFiltroCard.azioni.storico")}
              </Button>
            );
          },
        }),
      ].filter((x) => x),
    [i18n.language, t]
  );

  return (
    <Card
      sx={{
        display: "flex",
        alignSelf: "flex-start",
        minWidth: {
          xs: "100%",
          md: 400,
        },
        maxWidth: "100%",
        flex: 1,
      }}
    >
      <Box p={2} flex={1}>
        <Box display="flex" flexDirection="column" height="100%" gap={1}>
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap={1}
          >
            <Typography variant="h5" color="text" fontWeight="medium">
              {t("alloggiamentiFiltroCard.titolo")}
            </Typography>

            {hasPermission("ContenitoreUpdateAlloggiamentiFiltro") && (
              <Tooltip title={t("alloggiamentiFiltroCard.tooltip.modifica")}>
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setAlloggiamentiFiltroDialogOpen(true);
                  }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          <Table
            columns={columns}
            data={alloggiamentiFiltro}
            initialSort={[{ id: "nome", desc: false }]}
            paginated={false}
            noRowsText={t("alloggiamentiFiltroCard.noRows")}
            mt={1}
          />
        </Box>
      </Box>

      <AlloggiamentiFiltroDialog
        nodo={nodo}
        open={alloggiamentiFiltroDialogOpen}
        onClose={() => {
          setAlloggiamentiFiltroDialogOpen(false);
        }}
      />
      <StoricoAlloggiamentoFiltroDialog
        open={storicoFiltriDialogOptions.open}
        nodo={nodo}
        alloggiamentoFiltro={storicoFiltriDialogOptions.alloggiamento}
        onClose={() => {
          setStoricoFiltriDialogOptions({ open: false });
        }}
        canEdit={hasPermission("ContenitoreUpdateStoricoFiltri")}
      />
    </Card>
  );
}
